import { useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'
import { shouldBypassSupabaseRequests } from '../lib/runtimeConfig'

type UserProfile = Database['public']['Tables']['user_profiles']['Row']
const profileCache = new Map<string, EnhancedUserProfile>()
const getProfileCacheStorageKey = (userId: string) =>
  `printboard:user-profile:${userId}`

interface Address {
  name: string
  street: string
  city: string
  state: string
  zip: string
  country: string
}

interface EnhancedUserProfile extends UserProfile {
  shipping_address?: Address
  billing_address?: Address
  phone?: string
  preferences?: Record<string, unknown>
}

const readPersistedProfile = (userId?: string): EnhancedUserProfile | null => {
  if (!userId || typeof window === 'undefined') return null

  try {
    const rawValue = window.sessionStorage.getItem(
      getProfileCacheStorageKey(userId)
    )
    if (!rawValue) return null

    return JSON.parse(rawValue) as EnhancedUserProfile
  } catch {
    return null
  }
}

const writePersistedProfile = (
  userId: string,
  profile: EnhancedUserProfile | null
) => {
  if (typeof window === 'undefined') return

  try {
    if (!profile) {
      window.sessionStorage.removeItem(getProfileCacheStorageKey(userId))
      return
    }

    window.sessionStorage.setItem(
      getProfileCacheStorageKey(userId),
      JSON.stringify(profile)
    )
  } catch {
    // Ignore storage write failures.
  }
}

export function useUserProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<EnhancedUserProfile | null>(() =>
    user?.id ? profileCache.get(user.id) || readPersistedProfile(user.id) : null
  )
  const [loading, setLoading] = useState(() =>
    user?.id
      ? !profileCache.has(user.id) && !readPersistedProfile(user.id)
      : true
  )
  const [error, setError] = useState<string | null>(null)

  const syncProfile = (
    nextProfile: EnhancedUserProfile | null,
    userId?: string | null
  ) => {
    if (userId) {
      if (nextProfile) {
        profileCache.set(userId, nextProfile)
      } else {
        profileCache.delete(userId)
      }

      writePersistedProfile(userId, nextProfile)
    }

    setProfile(nextProfile)
  }

  useEffect(() => {
    if (!user) {
      syncProfile(null)
      setLoading(false)
      return
    }

    if (shouldBypassSupabaseRequests) {
      const devProfile: EnhancedUserProfile = {
        id: user.id,
        email: user.email || 'builder@printboard.io',
        display_name:
          user.user_metadata?.display_name ||
          user.user_metadata?.full_name ||
          user.email?.split('@')[0] ||
          'Developer',
        avatar_url: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        shipping_address: {},
        billing_address: {},
        preferences: {},
      }
      syncProfile(devProfile, user.id)
      setError(null)
      setLoading(false)
      return
    }

    const cachedProfile =
      profileCache.get(user.id) || readPersistedProfile(user.id)
    setError(null)
    if (cachedProfile) {
      syncProfile(cachedProfile, user.id)
      setLoading(false)
    } else {
      setLoading(true)
    }

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single()

        if (error) {
          if (error.code === 'PGRST116') {
            // Profile doesn't exist, create it
            const { data: newProfile, error: createError } = await supabase
              .from('user_profiles')
              .insert({
                id: user.id,
                email: user.email!,
                display_name:
                  user.user_metadata?.display_name ||
                  user.user_metadata?.full_name ||
                  user.email?.split('@')[0] ||
                  'User',
                shipping_address: {},
                billing_address: {},
                preferences: {},
              })
              .select('*')
              .single()

            if (createError) {
              throw createError
            }
            syncProfile(newProfile, user.id)
          } else {
            throw error
          }
        } else {
          syncProfile(data, user.id)
        }
      } catch (err) {
        if (!profileCache.has(user.id)) {
          setError(
            err instanceof Error ? err.message : 'Failed to load profile'
          )
          syncProfile(null, user.id)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [user])

  const updateProfile = async (updates: Partial<EnhancedUserProfile>) => {
    if (!user || !profile) return { error: 'No user or profile found' }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select('*')
        .single()

      if (error) throw error
      syncProfile(data as EnhancedUserProfile, user.id)
      return { data, error: null }
    } catch (err) {
      const error =
        err instanceof Error ? err.message : 'Failed to update profile'
      return { data: null, error }
    }
  }

  const updateAddress = async (
    type: 'shipping' | 'billing',
    address: Address
  ) => {
    if (!user || !profile) return { error: 'No user or profile found' }

    try {
      const updateField =
        type === 'shipping' ? 'shipping_address' : 'billing_address'
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ [updateField]: address })
        .eq('id', user.id)
        .select('*')
        .single()

      if (error) throw error
      syncProfile(data as EnhancedUserProfile, user.id)
      return { data, error: null }
    } catch (err) {
      const error =
        err instanceof Error ? err.message : 'Failed to update address'
      return { data: null, error }
    }
  }

  const updatePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    try {
      // First, get the current user's email
      const {
        data: { user: currentUser },
        error: userError,
      } = await supabase.auth.getUser()

      if (userError || !currentUser?.email) {
        return { error: 'Unable to verify user session' }
      }

      // Re-authenticate with current password to verify it's correct
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentUser.email,
        password: currentPassword,
      })

      if (signInError) {
        // Check if it's an authentication error
        if (
          signInError.message.includes('Invalid login credentials') ||
          signInError.message.includes('invalid')
        ) {
          return { error: 'Current password is incorrect' }
        }
        return { error: signInError.message }
      }

      // If re-authentication succeeds, update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        // Handle specific password update errors
        if (updateError.message.includes('weak password')) {
          return {
            error:
              'Please choose a stronger password with a mix of letters, numbers, and symbols',
          }
        }
        if (updateError.message.includes('Password should be')) {
          return { error: 'Password must be at least 6 characters long' }
        }
        throw updateError
      }

      return { error: null }
    } catch (err) {
      const error =
        err instanceof Error ? err.message : 'Failed to update password'
      return { error }
    }
  }

  return {
    profile,
    loading,
    error,
    updateProfile,
    updateAddress,
    updatePassword,
  }
}
